import React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import useStore from './store';
import { ServiceList } from './components/ServiceList';
import { EndpointList } from './components/EndpointList';
import { RequestList } from './components/RequestList';
import { ServiceForm } from './components/ServiceForm';
import { OperationForm } from './components/OperationForm';
import { EndpointForm } from './components/EndpointForm';
import { RequestEditor } from './components/RequestEditor';
import { HomePage } from './components/HomePage';
import { useStyletron } from 'baseui';
import { Navigation } from 'baseui/side-navigation';
import { Spinner } from 'baseui/spinner';

export function App() {
  const store = useStore();
  const [css] = useStyletron();
  const location = useLocation();
  const navigate = useNavigate();
  const showNavigation = !location.pathname.match(/^\/?(home)?$/);

  const servicesFetched = React.useRef(false);
  const endpointsFetched = React.useRef(false);
  const requestsFetched = React.useRef(false);

  // Load initial data
  const loadInitialData = React.useCallback(async () => {
    if (!showNavigation) return;
    
    try {
      // Always fetch services as they're needed for multiple routes
      if (!store.services?.length && !servicesFetched.current && !store.error) {
        servicesFetched.current = true;
        await store.fetchServices();
      }

      // Load data based on current route
      if (location.pathname.startsWith('/endpoints')) {
        if (!store.endpoints?.length && !endpointsFetched.current && !store.error) {
          endpointsFetched.current = true;
          await store.fetchEndpoints();
        }
      } else if (location.pathname.startsWith('/requests')) {
        if (!store.endpoints?.length && !endpointsFetched.current && !store.error) {
          endpointsFetched.current = true;
          await store.fetchEndpoints();
        }
        if (!store.requests?.length && !requestsFetched.current && !store.error) {
          requestsFetched.current = true;
          await store.fetchRequests();
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, [location.pathname, showNavigation, store]);

  React.useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <div
      className={css({
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'rgb(245, 247, 250)'
      })}
    >
      {showNavigation && (
        <div className={css({
          width: '200px',
          borderRight: '1px solid #E2E2E2',
          padding: '2rem 0',
          backgroundColor: 'rgb(245, 247, 250)'
        })}>
          <Navigation
            items={[
              { title: 'Services', itemId: '/services' },
              { title: 'Endpoints', itemId: '/endpoints' },
              { title: 'Requests', itemId: '/requests' }
            ]}
            activeItemId={location.pathname.startsWith('/services') ? '/services' : 
                         location.pathname.startsWith('/endpoints') ? '/endpoints' : 
                         location.pathname.startsWith('/requests') ? '/requests' : '/'}
            onChange={({ item }) => navigate(item.itemId)}
            overrides={{
              NavItem: {
                style: ({ $theme, $active }) => ({
                  backgroundColor: $active ? $theme.colors.backgroundSecondary : 'transparent',
                  ':hover': {
                    backgroundColor: $theme.colors.backgroundSecondary
                  }
                })
              }
            }}
          />
        </div>
      )}
      <div
        className={css({
          flex: 1,
          padding: '2rem',
          overflow: 'auto'
        })}
      >
        {store.loading && (
          <div className={css({ display: 'flex', justifyContent: 'center', marginBottom: '1rem' })}>
            <Spinner />
          </div>
        )}
        
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services/*" element={<ServiceList />}>
            <Route path="new" element={<ServiceForm />} />
            <Route path=":serviceId/edit" element={<ServiceForm />} />
            <Route path="operations/new" element={<OperationForm />} />
            <Route path="operations/:operationId" element={<OperationForm />} />
          </Route>
          <Route path="/endpoints" element={<EndpointList />}>
            <Route path="new" element={<EndpointForm />} />
            <Route path=":endpointId/edit" element={<EndpointForm />} />
          </Route>
          <Route path="/requests" element={<RequestList />}>
            <Route path=":requestId/:tab" element={<RequestEditor />} />
            <Route path="new/:tab" element={<RequestEditor />} />
          </Route>
        </Routes>
      </div>
    </div>
  );
}

export default App;